import { CommonUtility } from '@utility/common';
import {
  POStatusNames,
  POStatusIndicator,
  POStatusColor,
  PoPublicStatusPopup,
  ShowUploadedDocs,
  POStatus,
} from '@utility/constants/common';
import { DateFormat, DateUtility } from '@utility/date';
import { DiscountRules } from './constants';
import type { IPOAttribute, IPOProduct } from '@hooks/purchase-order-items';

interface ICalculateTotalReturn {
  grossTotal: number;
  down_payment: number;
  upon_shipment: number;
  discount: number;
  total: number;
  down: number;
  upon: number;
  afterDiscountDown?: number;
  afterDiscountUpon: number;
}

export const calculateTotal = (
  grossTotal: number,
  upon_shipment: number,
  down_payment: number,
  discount: number,
  discountRule: string,
  paidAmount?: number,
): ICalculateTotalReturn => {
  let tempDiscount = 0;
  if (discountRule === DiscountRules.Fixed) {
    tempDiscount = discount;
  } else {
    tempDiscount = CommonUtility.roundNumber(
      (grossTotal * (discount || 0)) / 100,
      4,
    );
  }
  const total = grossTotal - tempDiscount;

  return {
    grossTotal,
    down_payment,
    upon_shipment,
    discount: tempDiscount,
    total: total < 0 ? 0 : total,
    down: CommonUtility.roundNumber((down_payment * total) / 100, 4),
    upon: CommonUtility.roundNumber((upon_shipment * total) / 100, 4),
    afterDiscountDown:
      total > 0
        ? (paidAmount || 0) > 0
          ? paidAmount
          : CommonUtility.roundNumber((down_payment * total) / 100, 4)
        : 0,
    afterDiscountUpon:
      total > 0
        ? (paidAmount || 0) > 0
          ? total - (paidAmount || 0)
          : CommonUtility.roundNumber((upon_shipment * total) / 100, 4)
        : 0,
  };
};

interface ICalculateItemAttribute {
  ctn_qty?: number;
  qty: number;
}

interface ICalculateItemReturn {
  ctn_qty: number;
  qty: number;
}

export const calculateItem = (
  attributes: ICalculateItemAttribute[],
): ICalculateItemReturn => {
  const total: ICalculateItemReturn = {
    ctn_qty: 0,
    qty: 0,
  };
  attributes.forEach(item => {
    total.ctn_qty += item.ctn_qty || 0;
    total.qty += item.qty;
  });
  return total;
};

interface ICalculateItemTotalItem {
  item_upc: string;
  ctn_qty?: number;
  qty?: number;
  total?: number;
  aliases: Record<string, ICalculateItemTotalAlias>;
}

interface ICalculateItemTotalAlias {
  alias: string;
  qty?: number;
  ctn_qty?: number;
}

interface ICalculateItemTotalMainItem {
  item_upc: string;
  attributes: ICalculateItemTotalAttribute[];
  currentAttributes: ICalculateItemTotalAttribute[];
}

type IDimension = {
  unit?: string;
  value?: number;
};

interface ICalculateItemTotalAttribute {
  alias: string;
  carton_gross_weight?: IDimension;
  carton_length?: IDimension;
  units_per_carton?: number;
}

interface ICalculateItemTotalTotal {
  cartons: number;
  units: number;
  retails: number;
  grossWeight: number;
}

interface ICalculateItemTotalReturn {
  total: ICalculateItemTotalTotal;
  cbm: number;
  cbf: number;
}

export const calculateItemTotal = (
  items: ICalculateItemTotalMainItem[],
  itemValue: Record<string, ICalculateItemTotalItem> | null | undefined,
): ICalculateItemTotalReturn => {
  const total: ICalculateItemTotalTotal = {
    cartons: 0,
    units: 0,
    retails: 0,
    grossWeight: 0,
  };
  let cbm = 0;
  let imperial = false;

  Object.values(itemValue || {}).forEach(item => {
    total.cartons += +(item.ctn_qty ?? 0);
    total.units += +(item.qty ?? 0);
    total.retails += item.total || 0;
    const mainItem = items.find(x => x.item_upc === item.item_upc);
    if (mainItem) {
      let totalGW = 0;
      Object.values(item.aliases).forEach(attribute => {
        if (attribute.qty) {
          let mainAttribute = mainItem.attributes.find(
            x => x.alias === attribute.alias,
          );
          if (!mainAttribute) {
            mainAttribute = mainItem.currentAttributes.find(
              x => x.alias === attribute.alias,
            );
          }
          const grossWeight = mainAttribute?.carton_gross_weight?.value || 0;
          totalGW += +(attribute.ctn_qty ?? 0) * grossWeight;
          if (mainAttribute?.carton_length?.unit === 'inch') {
            imperial = true;
            cbm += CommonUtility.cbf(mainAttribute, attribute.ctn_qty);
          } else {
            cbm += CommonUtility.cbm(mainAttribute, attribute.ctn_qty);
          }
        }
      });
      total.grossWeight += totalGW;
    }
  });

  const calulcatedCBM = imperial ? CommonUtility.cbfTocbm(cbm) : cbm;
  const calulcatedCBF = imperial ? cbm : CommonUtility.cbmTocbf(cbm);

  return { total, cbm: calulcatedCBM, cbf: calulcatedCBF };
};

interface IPoStatusFunctionReturn {
  title: string;
  indicator: number;
  color: string;
  publicText: string;
  showFiles: boolean | undefined;
}

export const PoStatusFunction = (
  status: string,
  publicStatus: string,
): IPoStatusFunctionReturn => {
  const excludedStatus = [
    POStatus.completed,
    POStatus.readyForShipment,
    POStatus.inProduction,
    POStatus.archived,
  ].includes(status as any);
  const statusText =
    status + (publicStatus && !excludedStatus ? `-${publicStatus}` : '');
  const PoStatus: IPoStatusFunctionReturn = {
    title: POStatusNames?.[statusText] || '',
    indicator: POStatusIndicator?.[status] || 0,
    color: POStatusColor?.[statusText] || 'var(--sk-danger-fg)',
    publicText: PoPublicStatusPopup?.[statusText] || '',
    showFiles: ShowUploadedDocs?.[statusText],
  };
  return PoStatus;
};

export const getCompletedEstimationDay = (
  estimated_completion: string | Date | null | undefined,
): string => {
  if (!estimated_completion) {
    return '-';
  }
  const diff = DateUtility.difference(new Date(), estimated_completion);

  if (diff > 30 || diff < -30) {
    return DateUtility.formatDate(estimated_completion, DateFormat.date);
  }
  if (diff < 0) {
    return `${-diff} days ago`;
  }
  return `In ${diff} days`;
};

interface IUnitValue {
  unit?: string;
  value?: number;
}

interface IAlias {
  carton_length: IUnitValue;
  carton_width: IUnitValue;
  carton_height: IUnitValue;
  carton_net_weight: IUnitValue;
  carton_gross_weight: IUnitValue;
  primary: boolean;
  state: string;
  units_per_carton: number;
  createdAt: string;
  _id: string;
  alias: string;
}

interface IPrice {
  moq?: number;
  price: number;
}

interface IMoqGrouping {
  items?: IValidatedMOQGroupingItem[];
  collective_moq?: number;
}

interface IAliasWithQty extends Partial<IAlias> {
  alias: string;
  ctn_qty?: number;
  qty: number;
  units_per_carton?: number;
  prediction?: boolean;
  unit_of_measurement?: string;
}

export interface IUPCQty {
  aliases: Record<string, IAliasWithQty>;
  item_upc: string;
  prediction: boolean;
  regionalized?: boolean;
  title: string;
  sku: string;
  image?: string;
  moq_grouping?: IMoqGrouping;
  ctn_qty?: number;
  qty?: number;
  current_price: number;
  prices: IPrice[];
  total?: number;
  MOQ?: number;
}

interface IPoQtyChangesUpcQty {
  [key: string]: IUPCQty;
}

interface IPoQtyChangesAttribute {
  alias: string;
  ctn_qty?: number;
  qty: number;
  units_per_carton?: number;
}

interface IPoQtyChangesItem {
  upc: string;
  attributes: IPoQtyChangesItemAttribute[];
  prices?: IPoQtyChangesPrice[];
  price?: number;
  predictedQty?: number;
}

interface IPoQtyChangesItemAttribute {
  alias: string;
  units_per_carton: number;
}

export interface IPoQtyChangesPrice {
  moq: number;
  price: number;
}

export const poQtyChanges = (
  upcQty: IPoQtyChangesUpcQty,
  item: IPoQtyChangesItem,
  alias: string,
  name: string,
  value: string | number,
): IPoQtyChangesUpcQty => {
  let temp = { ...upcQty[item.upc] };
  const latestAttribute = item.attributes.find(x => x.alias === alias);
  const tempAttributes: IPoQtyChangesAttribute = {
    ...latestAttribute,
  } as IPoQtyChangesAttribute;
  if (name === 'ctn_qty') {
    tempAttributes.ctn_qty = value !== '' ? +value : undefined;
    tempAttributes.qty =
      (tempAttributes.ctn_qty as number) *
      (latestAttribute?.units_per_carton || 1);
  } else {
    tempAttributes.qty = +value;
  }
  temp.aliases = {
    ...temp.aliases,
    [alias]: {
      ...latestAttribute,
      ...tempAttributes,
    } as IAliasWithQty,
  };

  const tempTotal = {
    current_price: 0,
    qty: 0,
    total: 0,
    prices: [] as IPoQtyChangesPrice[],
    ctn_qty: 0,
  };

  const aliasValues = Object.values(temp.aliases) as ICalculateItemAttribute[];
  const res = { ...calculateItem(aliasValues) };
  tempTotal.ctn_qty = res.ctn_qty;
  tempTotal.qty = res.qty;

  const tempPrices = [...(item.prices || [])].sort((a, b) => b.moq - a.moq);
  tempTotal.current_price =
    tempPrices.find(x => x.moq <= tempTotal.qty)?.price || item.price || 0;
  tempTotal.total = tempTotal.qty * tempTotal.current_price;
  tempTotal.prices = [...tempPrices];
  temp.prediction = !!item.predictedQty;
  temp = {
    ...temp,
    ...tempTotal,
  };

  return {
    ...upcQty,
    [item.upc]: { ...temp },
  };
};

interface IPoUpdatePredictedQtyItem {
  upc: string;
  predictedQty: number;
  attributes: IPoUpdatePredictedQtyItemAttribute[];
  prices?: IPoQtyChangesPrice[];
}

interface IPoUpdatePredictedQtyItemAttribute {
  alias: string;
  units_per_carton: number;
}

export const poUpdatePredictedQtyForItem = (
  upcQty: IPoQtyChangesUpcQty,
  item: IPoUpdatePredictedQtyItem,
): IPoQtyChangesUpcQty => {
  let temp = { ...upcQty[item.upc] };

  const tempAttributes: Record<string, IAliasWithQty> = {};
  item.attributes.forEach(attribute => {
    const tempA: IAliasWithQty = {
      ...temp.aliases[attribute.alias],
    };
    tempA.ctn_qty = Math.round(item.predictedQty / attribute.units_per_carton);
    tempA.qty = tempA.ctn_qty * attribute.units_per_carton;

    tempAttributes[attribute.alias] = tempA;
  });

  const tempTotal = {
    current_price: 0,
    qty: 0,
    total: 0,
    prices: [] as IPoQtyChangesPrice[],
    ctn_qty: 0,
  };

  const res = { ...calculateItem(Object.values(tempAttributes)) };
  tempTotal.ctn_qty = res.ctn_qty;
  tempTotal.qty = res.qty;

  const tempPrices = [...(item.prices || [])].sort((a, b) => b.moq - a.moq);
  tempTotal.current_price =
    tempPrices.find(x => x.moq <= tempTotal.qty)?.price || 0;
  tempTotal.total = tempTotal.qty * tempTotal.current_price;
  tempTotal.prices = [...tempPrices];

  temp = {
    ...temp,
    aliases: { ...tempAttributes },
    ...tempTotal,
  };

  return {
    ...upcQty,
    [item.upc]: { ...temp },
  };
};

interface IPoUpdateAllPredicateUpcQtyItem {
  aliases: Record<string, IPoUpdateAllPredicateAttribute>;
  [key: string]: unknown;
}

interface IPoUpdateAllPredicateAttribute {
  alias: string;
  ctn_qty: number;
  qty: number;
  prediction: boolean;
  units_per_carton?: number;
}

interface IPoUpdateAllPredicateItem {
  upc: string;
  item_upc: string;
  predictedQty?: number;
  attributes?: IPoUpdateAllPredicateItemAttribute[];
  prices?: IPoUpdateAllPredicatePrice[];
  MOQ?: number;
  p_title: string;
  p_sku: string;
  moq_grouping?: IMoqGrouping;
  p_images?: {
    display_image?: string;
  };
}

interface IPoUpdateAllPredicateItemAttribute {
  alias: string;
  units_per_carton: number;
}

interface IPoUpdateAllPredicatePrice {
  moq: number;
  price: number;
}

export const poUpdateAllPredicate = (
  upcQty: IPoQtyChangesUpcQty,
  items: IPoUpdateAllPredicateItem[],
): IPoQtyChangesUpcQty => {
  const qtyResult: IPoQtyChangesUpcQty = {};
  items.forEach(item => {
    if (item.predictedQty && (item.attributes || []).length > 0) {
      const tempAttributes: Record<string, IAliasWithQty> = {};
      item.attributes!.forEach(attribute => {
        const units = attribute.units_per_carton || 1;
        const temp = {
          ...upcQty[item.upc].aliases[attribute.alias],
        };
        temp.ctn_qty = Math.round(item.predictedQty! / units);
        temp.qty = temp.ctn_qty * units;
        temp.prediction = !!item.predictedQty;
        tempAttributes[attribute.alias] = temp;
      });

      const tempTotal = {
        current_price: 0,
        qty: 0,
        total: 0,
        prices: [] as IPoQtyChangesPrice[],
        ctn_qty: 0,
        MOQ: undefined as number | undefined,
      };

      const res = { ...calculateItem(Object.values(tempAttributes)) };
      tempTotal.ctn_qty = res.ctn_qty;
      tempTotal.qty = res.qty;

      const tempPrices = [...(item.prices || [])].sort((a, b) => b.moq - a.moq);
      tempTotal.current_price =
        tempPrices.find(x => x.moq <= tempTotal.qty)?.price || 0;
      tempTotal.prices = [...tempPrices];
      tempTotal.total = tempTotal.qty * tempTotal.current_price;
      tempTotal.MOQ = item.MOQ;

      qtyResult[item.upc] = {
        aliases: { ...tempAttributes },
        prediction: !!item.predictedQty,
        item_upc: item.item_upc,
        title: item.p_title,
        sku: item.p_sku,
        moq_grouping: item.moq_grouping,
        image: item.p_images?.display_image,
        ...tempTotal,
      };
    }
  });
  return {
    ...upcQty,
    ...qtyResult,
  };
};

interface IUpdateAttributeItem {
  item_upc: string;
  attributes: IUpdateAttributeAttribute[];
  aliases?: IUpdateAttributeAlias[];
  currentAttributes: IUpdateAttributeAttribute[];
}

interface IUpdateAttributeAttribute {
  alias: string;
  [key: string]: unknown;
}

interface IUpdateAttributeAlias {
  alias: string;
  attributes?: unknown;
  [key: string]: unknown;
}

export const updateAttribute = (
  items: IPOProduct[],
  currentItem?: IPOProduct,
  newAttribute?: IPOAttribute,
): IPOProduct[] => {
  const tempItems = items.map(item => {
    if (item.item_upc === currentItem?.item_upc) {
      const t = { ...item };
      t.attributes = t.attributes.map(attribute => {
        if (attribute.alias === newAttribute?.alias) {
          return { ...newAttribute };
        }
        return attribute;
      });
      t.aliases = (t.aliases || []).map(alias => {
        if (alias.alias === newAttribute?.alias) {
          return {
            ...alias,
            attributes: { ...newAttribute, alias: undefined },
          };
        }
        return alias;
      });
      t.currentAttributes = t.currentAttributes.map(attribute => {
        if (attribute.alias === newAttribute?.alias) {
          return { ...newAttribute };
        }
        return attribute;
      });
      return t;
    }
    return item;
  });
  return tempItems;
};

interface IAddAttributeItem {
  item_upc: string;
  attributes?: IAddAttributeAttribute[];
  currentAttributes: IAddAttributeAttribute[];
}

interface IAddAttributeAttribute {
  alias: string;
  [key: string]: unknown;
}

export const addAttribute = (
  items: IPOProduct[],
  currentItem?: IPOProduct,
  newAttribute?: IPOAttribute,
): IPOProduct[] => {
  if (!currentItem || !newAttribute) {
    return items;
  }
  const tempItems = items.map(item => {
    if (item.item_upc === currentItem?.item_upc) {
      const t = { ...item };
      t.attributes = [...(t.attributes || []), { ...newAttribute }];
      if (
        !currentItem.currentAttributes.some(
          x => x.alias === newAttribute?.alias,
        )
      ) {
        t.currentAttributes = [
          ...(t.currentAttributes || []),
          { ...newAttribute },
        ];
      } else {
        t.currentAttributes = t.currentAttributes.map(attribute => {
          if (attribute.alias === newAttribute?.alias) {
            return { ...newAttribute };
          }
          return attribute;
        });
      }
      return t;
    }
    return item;
  });
  return tempItems;
};

interface IDeleteAttributeAttribute {
  alias: string;
  [key: string]: unknown;
}

export const deleteAttribute = (
  items: IPOProduct[],
  currentItem: IPOProduct,
  alias: string,
): IPOProduct[] => {
  const tempItems = items.map(item => {
    if (item.item_upc === currentItem.item_upc) {
      const t = { ...item };
      t.attributes = (t.attributes || []).filter(x => x.alias !== alias);
      return t;
    }
    return item;
  });
  return tempItems;
};

interface ICalculateAdjustmentsAdjustment {
  value: number;
  unit: string;
  operation: string;
  [key: string]: unknown;
}

interface ICalculateAdjustmentsCalculatedAdjustment
  extends ICalculateAdjustmentsAdjustment {
  total: number;
}

interface ICalculateAdjustmentsReturn {
  adjustPricesTotal: number;
  calculatedAdjustments: ICalculateAdjustmentsCalculatedAdjustment[];
}

export const calculateAdjustments = (
  total: number,
  adjustments: ICalculateAdjustmentsAdjustment[] | null | undefined,
): ICalculateAdjustmentsReturn => {
  let tempTotal = 0;
  const tempAdjustment: ICalculateAdjustmentsCalculatedAdjustment[] = [];
  if (total > 0 && (adjustments || []).length > 0) {
    adjustments!.forEach(item => {
      let value = item.value;
      const temp = total + tempTotal;
      if (item.unit === 'percentage') {
        value = (temp * item.value) / 100;
      }
      if (item.operation === 'add') {
        tempTotal += value;
      } else {
        tempTotal -= value;
      }
      tempAdjustment.push({
        ...item,
        total: value * (item.operation === 'add' ? 1 : -1),
      });
    });
  }
  return {
    adjustPricesTotal: CommonUtility.roundNumber(tempTotal, 4),
    calculatedAdjustments: tempAdjustment,
  };
};

interface IValidatedMOQItem {
  upc?: string;
  item_upc?: string;
  MOQ?: number;
  moq_grouping?: IMoqGrouping;
}

interface IValidatedMOQGroupingItem {
  upc?: string;
  item_upc?: string;
}

interface IValidatedMOQUpcQty {
  [key: string]: {
    qty?: number;
  };
}

interface IValidatedMOQAttribute {
  units_per_carton?: number;
}

export const validatedMOQ = (
  item: IValidatedMOQItem,
  upcQty: IValidatedMOQUpcQty,
  latestAttribute?: IValidatedMOQAttribute,
): string | null => {
  const currentMOQ = item.MOQ;
  if (
    (item.moq_grouping?.items || []).length > 0 &&
    item.moq_grouping?.items?.every(
      itemMOQ => upcQty[itemMOQ.upc || itemMOQ.item_upc!]?.qty,
    )
  ) {
    const totalQty = item.moq_grouping.items!.reduce(
      (a, b) => a + (upcQty[b.upc || b.item_upc!]?.qty ?? 0),
      0,
    );
    if (totalQty < (item.moq_grouping.collective_moq ?? 0)) {
      return `You must enter a Collective MOQ greater than ${CommonUtility.numberWithCommas(
        item.moq_grouping.collective_moq! - 1,
      )}`;
    }
  } else if (
    currentMOQ &&
    currentMOQ > +(upcQty[item.upc || item.item_upc!]?.qty ?? 0)
  ) {
    let cartons = 0;
    if (CommonUtility.isValidNumber(latestAttribute?.units_per_carton)) {
      cartons = Math.ceil(currentMOQ / latestAttribute!.units_per_carton!) - 1;
    }
    return `You must enter a Carton Quantity greater than ${CommonUtility.numberWithCommas(
      cartons,
    )}`;
  }
  return null;
};
